/**
 * Convert suffix-encoded filter entries to raw OpenSearch DSL clauses.
 *
 * Mirrors the per-suffix dispatch in page.gts/preview-table.gts but returns
 * clause objects instead of mutating an OpenSearchQueryBuilder. Same logic,
 * different output, so the result can be composed into AND or OR groups.
 */

import { normalizeMultiFilterValue } from './filter-operators';
import type { FilterClause } from 'tio-common/utils/open-search-builder';
import type { FieldOption } from './opensearch-field-mapper';

export interface FilterClauseContext {
  /** Resolve a field key to its OpenSearch path */
  pathFor: (key: string) => string;
  /** Field metadata for queryHints-driven dispatch on plain (no-suffix) keys */
  fieldByKey: Record<string, FieldOption>;
}

/**
 * Build one or more FilterClause objects for a single filterValues entry.
 *
 * Returns clauses already wrapped for must_not semantics (e.g. ne, not_in,
 * is_empty) inside a `{ bool: { must_not: [...] } }` so callers can place
 * any clause uniformly into either bool.filter (AND) or bool.should (OR).
 *
 * Returns an empty array if the value is empty/null/undefined.
 */
export function buildClausesForEntry(
  key: string,
  value: unknown,
  ctx: FilterClauseContext
): FilterClause[] {
  if (value === undefined || value === null || value === '') return [];
  // An empty multi-select is "no constraint" for every operator — without this
  // a plain-key empty array falls through to `terms: []` (or `term: ''` via
  // String([])), which matches zero documents.
  if (Array.isArray(value) && value.length === 0) return [];

  // Relative-date presets must be resolved to gte/lte upstream
  // (resolveRelativeDateFilters). If one slips through, skip it rather than
  // emit a meaningless term on the `__relative_date` key (which matches nothing).
  if (key.endsWith('__relative_date')) return [];

  if (key.endsWith('__is_empty')) {
    const field = ctx.pathFor(key.replace(/__is_empty$/, ''));
    return [{ bool: { must_not: [{ exists: { field } }] } }];
  }

  if (key.endsWith('__is_not_empty')) {
    const field = ctx.pathFor(key.replace(/__is_not_empty$/, ''));
    return [{ exists: { field } }];
  }

  if (key.endsWith('__ne')) {
    const field = ctx.pathFor(key.replace(/__ne$/, ''));
    const v = typeof value === 'boolean' ? value : String(value);
    return [{ bool: { must_not: [{ term: { [field]: v } }] } }];
  }

  if (key.endsWith('__not_in')) {
    const field = ctx.pathFor(key.replace(/__not_in$/, ''));
    const values = normalizeMultiFilterValue(value);
    if (values.length === 0) return [];
    return [{ bool: { must_not: [{ terms: { [field]: values } }] } }];
  }

  if (key.endsWith('__in')) {
    const field = ctx.pathFor(key.replace(/__in$/, ''));
    const values = normalizeMultiFilterValue(value);
    if (values.length === 0) return [];
    return [{ terms: { [field]: values } }];
  }

  if (key.endsWith('__gt')) {
    const field = ctx.pathFor(key.replace(/__gt$/, ''));
    const v = typeof value === 'number' ? value : String(value);
    return [{ range: { [field]: { gt: v } } }];
  }

  if (key.endsWith('__gte')) {
    const field = ctx.pathFor(key.replace(/__gte$/, ''));
    return [{ range: { [field]: { gte: String(value) } } }];
  }

  if (key.endsWith('__lt')) {
    const field = ctx.pathFor(key.replace(/__lt$/, ''));
    const v = typeof value === 'number' ? value : String(value);
    return [{ range: { [field]: { lt: v } } }];
  }

  if (key.endsWith('__lte')) {
    const field = ctx.pathFor(key.replace(/__lte$/, ''));
    return [{ range: { [field]: { lte: String(value) } } }];
  }

  if (key.endsWith('__contains')) {
    const field = ctx.pathFor(key.replace(/__contains$/, ''));
    return [{ wildcard: { [field]: `*${String(value)}*` } }];
  }

  if (key.endsWith('__starts_with')) {
    const field = ctx.pathFor(key.replace(/__starts_with$/, ''));
    return [{ prefix: { [field]: String(value) } }];
  }

  // Plain key — dispatch via queryHints.filterClause (same as flat path)
  const field = ctx.pathFor(key);
  const clauseType = ctx.fieldByKey[key]?.queryHints?.filterClause;
  switch (clauseType) {
    case 'match':
      return [{ match: { [field]: String(value) } }];
    case 'range':
      return [{ range: { [field]: { gte: String(value), lte: String(value) } } }];
    case 'terms': {
      const values = Array.isArray(value)
        ? (value as Array<string | number>)
        : [value as string | number];
      return [{ terms: { [field]: values } }];
    }
    default:
      if (typeof value === 'boolean') {
        return [{ term: { [field]: value } }];
      }
      return [{ term: { [field]: String(value) } }];
  }
}

/**
 * Build all clauses for a single base field key by collecting every
 * suffix-encoded entry that resolves to that base.
 *
 * Used for OR-group members where a single field may contribute multiple
 * clauses (e.g. `between` produces both gte and lte entries).
 */
export function buildClausesForField(
  baseFieldKey: string,
  filterValues: Record<string, unknown>,
  ctx: FilterClauseContext
): FilterClause[] {
  const clauses: FilterClause[] = [];
  for (const [key, value] of Object.entries(filterValues)) {
    if (key !== baseFieldKey && !key.startsWith(`${baseFieldKey}__`)) continue;
    clauses.push(...buildClausesForEntry(key, value, ctx));
  }
  return clauses;
}
